/**
 * App sections — ONE list that drives both the app bar's nav and the route table.
 *
 * Why one list: a nav item with no route 404s, and a route with no nav item is
 * unreachable. Deriving both from the same array makes those states impossible,
 * and makes "add a page" a one-object change.
 *
 * Per-mode segregation: CCA and EIA do not expose the same sections. Rather than
 * forking the route table (which the variants rule forbids), each section
 * declares who it's for via `only`/`not`, and `getSections(mode)` filters. The
 * router then simply never registers a section the current product doesn't have,
 * so an EIA user can't reach a CCA-only page by typing its URL.
 *
 * Section shape:
 *   title      (required) — visible label in the app bar
 *   path       (required) — route path, basename-relative ('/explorer', NOT
 *                           '/cca/explorer' — the router adds the variant prefix)
 *   page       (required) — lazyWithPreload component; used as the route element
 *                           AND as the PrefetchLink preload target
 *   only / not (optional) — 'cca' | 'eia' | array. Omit to include in both.
 *   nav        (optional) — false to route it without showing it in the bar
 *                           (detail pages, deep links)
 *   permission (optional) — wrap the route in RequirePermission
 *   end        (optional) — exact-match the active state instead of prefix-match
 *   isDefault  (optional) — the default section: where a fresh sign-in lands.
 *                           Exactly one. ('/' itself is the Home page.)
 *   fullBleed  (optional) — true to skip the centred, padded <PageContainer>.
 *                           Set it for a page with a side rail, which needs the
 *                           rail flush against the screen edge.
 */
import {
  AnalyticsV2Page,
  CloudUsageReportsPage,
  CostAdvicePage,
  ExplorerPage,
  ManagePortfolioPage,
  RegionsListPage,
  ReleaseNotesPage,
  SupportPage,
  TelemetryPage,
} from '@router/lazyPages';
import { can, PERMISSIONS } from '@security/permissions';

export const APP_SECTIONS = Object.freeze([
  /*
   * Manage Portfolio is in BOTH products.
   *
   * Legacy's desktop EIA nav is empty, but that is the discarded-array bug at
   * Headerbar.vue:427 (EIA push commented out at :409), not a product decision.
   * Two things settle it:
   *  - EIA has its OWN help document for this page — helperModal.vue:69 maps
   *    '/dashboard/manageaccounts' to 'EIAManageAccounts' in the EIA branch;
   *  - it is where every post-login redirect lands, with no variant branch
   *    (Login.vue:188/:198, Home.vue:220, NotFound.vue:53, the router guard).
   * An EIA user is put on this page the moment they sign in.
   */
  Object.freeze({
    title: 'Manage Portfolio',
    path: '/manage-portfolio',
    page: ManagePortfolioPage,
    isDefault: true,
    fullBleed: true, // has the dashboard side rail
  }),

  /*
   * Explorer is CCA-only.
   *
   * Two independent signals, against the one variant-neutral feature flag in
   * navItems: menuItems adds it inside `if (isCCA())` (Headerbar.vue:364), and
   * helperModal.vue lists '/explorer' only in the CCA route map — EIA's branch
   * has just Manage Portfolio and Cost Advice (:68-71).
   *
   * `only` keeps it out of the route table too, so an EIA user cannot reach it
   * by typing the URL — which they can in legacy, where the route has no
   * variant filter at all.
   */
  Object.freeze({
    title: 'Explorer',
    path: '/explorer',
    page: ExplorerPage,
    only: 'cca',
    // Full screen width, not the centred column: a wide price table with up
    // to 14 columns (OCI Flex) needs the room. The page pads itself.
    fullBleed: true,
  }),

  /*
   * Analytics is role-gated, and `permission` here does two jobs: it hides the
   * nav item AND wraps the route in RequirePermission.
   *
   * Both matter. Legacy only hides the links — /analytics and /analytics/v2 are
   * declared at router/index.js:77-86 with no `meta.requiresAuth` and no role
   * check at all, so anyone can open either page by typing the URL while signed
   * out. Hiding a link is not access control.
   *
   * Who actually holds VIEW_ANALYTICS differs per product; that lives in
   * features/auth/roles.js, which is the only place that knows EIA withholds it
   * from super_admin.
   */
  /*
   * Analytics v2 keeps legacy's address, /analytics/v2 (router/index.js:82),
   * and the VIEW_ANALYTICS guard (user decision 2026-10-15). fullBleed: the
   * page has a full-width sticky header and pads its own sections.
   */
  Object.freeze({
    // Shown as 'Analytics' (user order 2026-10-16; legacy 'Analytics v2' —
    // v1 is gone). The address stays legacy's /analytics/v2.
    title: 'Analytics',
    path: '/analytics/v2',
    page: AnalyticsV2Page,
    permission: PERMISSIONS.VIEW_ANALYTICS,
    fullBleed: true,
  }),
  // Analytics v1 removed (user order 2026-10-16: "we don't need it any more") —
  // no nav link and no route; /analytics-v1 now shows the 404 page.

  /*
   * Telemetry — set up a connector (Datadog, CloudWatch, …) as a portfolio's
   * source. Both products. Not in the bar: picking a connector in the
   * sidebar's Service Provider opens it (legacy /dashboard/telemetry?type=).
   * Has the dashboard side rail, hence fullBleed. The feature switches it
   * needs (telemetry/connection, telemetry/metrics) are checked in the page.
   */
  Object.freeze({
    title: 'Telemetry',
    path: '/telemetry',
    page: TelemetryPage,
    nav: false,
    end: true,
    fullBleed: true,
  }),

  /*
   * Cloud Usage Reports — add a portfolio from a cloud account (legacy
   * /dashboard/cloudreports). CCA only: legacy's link is inside `!application`
   * (ManageAccount.vue:51), and `only` keeps the page out of EIA's route table
   * too. Not in the bar: Manage Portfolio's "Cloud Usage Reports" link opens
   * it. Has the side rail, hence fullBleed. Its feature switch
   * (manage_portfolio.cloud-accounts) is checked in the page — `permission`
   * is for roles, not FeaturesData switches.
   */
  Object.freeze({
    title: 'Cloud Usage Reports',
    path: '/cloud-usage-reports',
    page: CloudUsageReportsPage,
    only: 'cca',
    nav: false,
    end: true,
    fullBleed: true,
  }),

  // Routed but not in the bar — reachable by URL and by links from other pages.
  /*
   * Support. Both products, and not in the nav bar: legacy reaches it from the
   * app bar's headset icon and the profile menu, never from a top-level link.
   */
  Object.freeze({
    title: 'Support',
    path: '/support',
    page: SupportPage,
    nav: false,
    end: true,
  }),
  /*
   * Regions list. Both products, not in the bar: opened in a new tab from the
   * instance table's "List of <provider> Regions →" link, with the provider in
   * the address (?providerName=AWS). Legacy's path is /regionLists.
   */
  Object.freeze({
    title: 'Regions',
    path: '/regions-list',
    page: RegionsListPage,
    nav: false,
    end: true,
    // Full screen width, not the centred column: four reference tables side by
    // side and a 4-across instance grid need the room. The page pads itself.
    fullBleed: true,
  }),
  /* Reached from the Release Notes panel on the support page. */
  Object.freeze({
    title: 'Release Notes',
    path: '/support/release-notes',
    page: ReleaseNotesPage,
    nav: false,
    end: true,
  }),
  /*
   * Instance Advice (EIA) / Cost Advice (CCA) for one saved portfolio, by
   * ?portfolioId= (legacy /dashboard/costadvice). Both products. Not in the
   * bar: reached from a portfolio's Instance Advice button, or a finished
   * large-data portfolio in the sidebar. Has the side rail, hence fullBleed.
   * Its feature switch (manage_portfolio.cost-advice) is checked in the page.
   */
  Object.freeze({
    title: 'Cost Advice',
    path: '/cost-advice',
    page: CostAdvicePage,
    nav: false,
    end: true,
    fullBleed: true,
  }),
]);

function toArray(v) {
  if (v == null) return null;
  return Array.isArray(v) ? v : [v];
}

/** Does this section belong to `mode`? Same only/not semantics as VariantGate. */
function allowedInMode(section, mode) {
  const only = toArray(section.only);
  const not = toArray(section.not);
  if (only && !only.includes(mode)) return false;
  if (not && not.includes(mode)) return false;
  return true;
}

/** Every section this product exposes — the routable set. */
export function getSections(mode) {
  return APP_SECTIONS.filter((s) => allowedInMode(s, mode));
}

/**
 * The subset shown in the app bar, in order.
 *
 * `roles` is optional so callers that only need the shape (tests, the route
 * table) need not supply it — but the app bar MUST pass them, or a user sees a
 * link that immediately 403s at the route guard. Omitting roles hides every
 * permission-gated item rather than revealing it: denying by default is the
 * direction that cannot leak.
 */
export function getNavItems(mode, roles = []) {
  return getSections(mode).filter(
    (s) => s.nav !== false && (!s.permission || can(roles, s.permission)),
  );
}

/**
 * Where '/' should land for this product. Falls back to the first nav item if
 * the default section is filtered out of this mode.
 */
export function getDefaultPath(mode) {
  const sections = getSections(mode);
  const fallback = sections.find((s) => s.nav !== false) ?? sections[0];
  return (sections.find((s) => s.isDefault) ?? fallback)?.path ?? '/';
}
